import { Box, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import {
  ProjectManagementFinancialDetailsFormQuery,
  ProjectManagementProjectDetailsPageSettingsPageQuery,
} from 'generated/resources/graphql'
import { orderBy } from 'lodash'

import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import LabeledInput from 'modules/core/components/LabeledInput'
import MoneyField from 'modules/core/components/MoneyField'
import Select from 'modules/core/components/Select'
import { useEffect } from 'modules/core/hooks/useEffect'
import { FormErrors } from 'modules/core/hooks/useForm'
import i18n from 'modules/core/lib/i18n'

import {
  DEFAULT_CURRENCY,
  financialDetailsAttributes,
} from 'modules/project_management/lib/constants'

export interface IProjectFinancialDetailsFormProps {
  data?:
    | ProjectManagementFinancialDetailsFormQuery
    | ProjectManagementProjectDetailsPageSettingsPageQuery
  onChangeValue: (
    fieldName: financialDetailsAttributes,
    fieldValue: boolean | string | undefined
  ) => void
  values: IProjectFinancialDetailsValues
  errors: FormErrors<IProjectFinancialDetailsValues>
  isOwner: boolean
}

export interface IProjectFinancialDetailsValues {
  fixedCost: string | undefined
  isBillable: boolean
  legalEntityId: string
}

const ProjectFinancialDetailsForm = ({
  data,
  onChangeValue,
  values,
  errors,
  isOwner,
}: IProjectFinancialDetailsFormProps) => {
  const currencies = data?.apiCore.currencies
  const legalEntities = data?.apiCore.company?.legalEntities

  const createCurrencyOptions = () => {
    if (!legalEntities || !currencies) return null

    const options = orderBy(legalEntities, 'main', 'desc').map((legalEntity) => {
      if (legalEntity?.currency) null
      const currency = currencies.find((value) => value.isoCode === legalEntity?.currency)
      return {
        label: `${legalEntity?.legalName} - ${currency?.symbol} ${currency?.name}`,
        value: `${legalEntity?.id}`,
      }
    })
    return options.filter((option) => option !== null)
  }

  const currencyOptions = createCurrencyOptions()

  const getCurrencyType = () => {
    const currencyType =
      legalEntities?.find((le) => `${le.id}` === values.legalEntityId)?.currency ||
      legalEntities?.[0]?.currency ||
      DEFAULT_CURRENCY

    return currencyType
  }
  const currencyTypeSelected = getCurrencyType()

  useEffect(() => {
    onChangeValue(financialDetailsAttributes.currencyType, currencyTypeSelected)
  }, [currencyTypeSelected])

  return (
    <Box gap="s16">
      {currencyOptions && (
        <>
          <Select
            label={i18n.t('project_management.forms.financial_details.legal_entity.label')}
            secondaryLabel={
              <InformationTooltip
                information={i18n.t(
                  'project_management.forms.financial_details.legal_entity.information'
                )}
              />
            }
            options={currencyOptions.map((option) => ({
              label: option.label,
              value: option.value,
            }))}
            defaultValue={values.legalEntityId}
            onChange={(value) => onChangeValue(financialDetailsAttributes.legalEntityId, value)}
            readonly={!isOwner}
          />
          {isOwner && (
            <LabeledInput
              isLegend
              label={i18n.t('project_management.forms.financial_details.fixed_costs.label')}
              secondaryLabel={
                <InformationTooltip
                  information={i18n.t(
                    'project_management.forms.financial_details.fixed_costs.information'
                  )}
                />
              }
            >
              <MoneyField
                currency={currencyTypeSelected}
                placeholder="0"
                onChange={(value) => onChangeValue(financialDetailsAttributes.fixedCost, value)}
                value={values.fixedCost}
                error={errors.fixedCost}
                readonly={!isOwner}
                maxDecimals={2}
              />
            </LabeledInput>
          )}
        </>
      )}
      <TogglerCheckbox
        label={i18n.t('project_management.forms.financial_details.billableToggle.label')}
        value={values.isBillable}
        labelBefore
        onChange={(value) => onChangeValue(financialDetailsAttributes.isBillable, value)}
        disabled={!isOwner}
      ></TogglerCheckbox>
    </Box>
  )
}

export default ProjectFinancialDetailsForm
